import { ColDef } from "ag-grid-community";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import {
  BillingFilterData,
  PaymentStatusFilterType,
} from "../../types/appointment";
import { TReceiptEntity } from "../../types/billing";
import getBillingQueueColumnDefinitions from "../../utils/get-billing-queue-col-defs";
import { BillingInitStore, BillingStore, QueueColDef } from "./types";
import { getTimeFilterMeta, paymentFiltersObj } from "./utils";

const getInitState = (): BillingInitStore => {
  return {
    clinicId: "",
    receiptEntities: [],
    areFiltersApplied: false,
    paymentTimeline: "today",
    paymentTimelineDuration: getTimeFilterMeta("today"),
    paymentGroupFilter: "all-receipts",
    searchFilterString: "",
    paymodeFilters: [],
    serviceFilters: [],
    filteredReceiptEntities: [],
    isReceiptEntitiesLoading: true,
    showColumnSelector: false,
    lastUpdatedAt: null,
  };
};

const useBillingStore = create<BillingStore>()(
  persist(
    (set, get) => ({
      clinicId: "",
      setClinicId: ({ clinicId }) => set({ clinicId }),
      receiptEntities: [],
      areFiltersApplied: false,
      paymentTimeline: "today",
      paymentTimelineDuration: getTimeFilterMeta("today"),
      setPaymentTimelineDuration: ({ paymentTimeline, duration }) => {
        set({
          paymentTimeline,
          paymentTimelineDuration:
            paymentTimeline === "custom"
              ? duration
              : getTimeFilterMeta(paymentTimeline),
        });
      },
      paymentGroupFilter: "all-receipts",
      setPaymentGroupFilter: (filterGroup) =>
        set({ paymentGroupFilter: filterGroup }),
      searchFilterString: "",
      setSearchFilterString: (namePhoneString) =>
        set({ searchFilterString: namePhoneString }),
      paymodeFilters: [],
      setPaymodeFilters: (filterValues) => {
        set({ paymodeFilters: filterValues });
      },
      serviceFilters: [],
      setServiceFilters: (filterValues) => {
        set({ serviceFilters: filterValues });
      },
      filteredReceiptEntities: [],
      setReceiptEntity: ({ idx, receiptEntity }) => {
        const { receiptEntities: currReceiptEntities } = get();
        const receiptEntityIndex = currReceiptEntities.findIndex(
          (curr) => curr.idx === idx
        );
        if (receiptEntityIndex > -1) {
          const updatedReceiptEntities = [
            ...currReceiptEntities.slice(0, receiptEntityIndex),
            receiptEntity,
            ...currReceiptEntities.slice(receiptEntityIndex + 1),
          ];
          set({
            receiptEntities: updatedReceiptEntities,
          });
        }
      },
      setReceiptEntities: ({
        receiptEntities,
        isReceiptEntitiesLoading = false,
      }) => {
        const filterData = {} as BillingFilterData;

        (Object.keys(paymentFiltersObj) as PaymentStatusFilterType[]).forEach(
          (k) => {
            const { filteredReceiptEntities, areFiltersApplied } = get();

            const t = paymentFiltersObj[k].getData(
              areFiltersApplied ? filteredReceiptEntities : receiptEntities
            );

            filterData[k] = {
              count: t.receiptEntities.length,
              amount: t.amount,
            };
          }
        );

        set({
          receiptEntities,
          isReceiptEntitiesLoading,
          filterMeta: filterData,
        });
      },
      isReceiptEntitiesLoading: true,
      queueColumnDefinitions: getBillingQueueColumnDefinitions(),
      setQueueColumnDefinitions: (queueColumnDefinition: QueueColDef) =>
        set({ queueColumnDefinitions: queueColumnDefinition }),
      showColumnSelector: false,
      setShowColumnSelector: (show: boolean) =>
        set({ showColumnSelector: show }),
      resetStore: () => {
        set(getInitState());
      },
      showCounterPopup: false,
      setShowCounterPopup: (popupState) => {
        set({ showCounterPopup: popupState });
      },
      lastUpdatedAt: null,
      setLastUpdatedAt: (date) => {
        set({ lastUpdatedAt: date });
      },
    }),
    {
      name: "billing-queue-config",
      storage: createJSONStorage(() => localStorage),
      partialize: ({ queueColumnDefinitions }) => {
        const colDefinitionsToBePersisted = queueColumnDefinitions?.map(
          (definition) => {
            const { colId, width, hide } = definition as ColDef<TReceiptEntity>;
            return { colId, width, hide };
          }
        );
        return { queueColumnDefinitions: colDefinitionsToBePersisted };
      },
      merge: (persistedState, currentState) => {
        const defaultStateIndexMap = new Map<string, number>();
        const defaultQueueColDefs =
          currentState.queueColumnDefinitions as ColDef<TReceiptEntity>[];

        defaultQueueColDefs?.forEach(({ colId }, index) => {
          if (colId) {
            defaultStateIndexMap.set(colId, index);
          }
        });

        const persistedStateIndexMap = new Map<string, number>();
        const persistedQueueColDefs = (
          persistedState as Record<
            "queueColumnDefinitions",
            Pick<ColDef, "colId" | "width" | "hide">
          >
        ).queueColumnDefinitions as Pick<
          ColDef<TReceiptEntity>,
          "colId" | "width" | "hide"
        >[];

        persistedQueueColDefs?.forEach(({ colId }, index) => {
          if (colId) {
            persistedStateIndexMap.set(colId, index);
          }
        });

        const newUpdatedColDefs: ColDef<TReceiptEntity>[] = [];
        const newlyAddedColDefs: ColDef<TReceiptEntity>[] = [];

        defaultQueueColDefs.forEach((defColDef) => {
          const { colId } = defColDef;

          let colDefFound: boolean = false;

          if (colId && persistedStateIndexMap.has(colId)) {
            const persistedIndex = persistedStateIndexMap.get(colId);
            if (typeof persistedIndex !== "undefined" && persistedIndex > -1) {
              const persistedQueueColItem =
                persistedQueueColDefs[persistedIndex];
              const { width, hide } = persistedQueueColItem;
              newUpdatedColDefs[persistedIndex] = { ...defColDef, width, hide };
              colDefFound = true;
            }
          }

          if (!colDefFound) {
            newlyAddedColDefs.push(defColDef);
          }
        });

        return {
          ...currentState,
          queueColumnDefinitions: [...newUpdatedColDefs, ...newlyAddedColDefs],
        };
      },
    }
  )
);

export default useBillingStore;
